/**
 * FilmGrain — overlay de textura cinematográfica
 *
 * Adiciona grão de filme animado via SVG filter + noise2D para
 * look cinematográfico. Elimina o "clean CGI" das compositions.
 *
 * Uso:
 * <FilmGrain opacity={0.06} animated />
 * <FilmGrain opacity={0.1} animated={false} />
 */
import React from "react";
import {AbsoluteFill, useCurrentFrame} from "remotion";
import {noise2D} from "@remotion/noise";

export interface FilmGrainProps {
  /** Opacidade do grain (0.03–0.12 recomendado) */
  opacity?: number;
  /** Animar o grain frame a frame (true) ou usar textura estática (false) */
  animated?: boolean;
  /** Blend mode — 'overlay' para sutil, 'screen' para glow */
  blendMode?: "overlay" | "screen" | "multiply" | "soft-light";
}

export const FilmGrain: React.FC<FilmGrainProps> = ({
  opacity = 0.06,
  animated = true,
  blendMode = "overlay",
}) => {
  const frame = useCurrentFrame();
  // Muda o seed a cada frame para animar o grain
  const seed = animated ? frame * 1.7 : 0;

  // SVG filter com feTurbulence para grão determinístico
  const filterId = `grain-${seed}`;
  const baseFreq = 0.65 + noise2D("grain-bf", seed / 100, 0) * 0.1;

  return (
    <AbsoluteFill
      style={{
        mixBlendMode: blendMode,
        opacity,
        pointerEvents: "none",
      }}
    >
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <filter id={filterId} x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency={baseFreq}
            numOctaves={4}
            seed={seed}
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter={`url(#${filterId})`} />
      </svg>
    </AbsoluteFill>
  );
};

/**
 * Vignette — escurecimento nas bordas (cinematográfico)
 */
export interface VignetteProps {
  opacity?: number;
  spread?: number;  // tamanho do fade (px)
}

export const CinemaVignette: React.FC<VignetteProps> = ({
  opacity = 0.55,
  spread = 250,
}) => (
  <AbsoluteFill
    style={{
      background: `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,${opacity}) 100%)`,
      pointerEvents: "none",
    }}
  />
);

/**
 * Letterbox — barras cinematográficas 2.39:1
 */
export interface LetterboxProps {
  ratio?: "2.39:1" | "2.35:1" | "1.85:1";
  color?: string;
}

export const Letterbox: React.FC<LetterboxProps> = ({
  ratio = "2.39:1",
  color = "#000000",
}) => {
  // Para 1080px de altura:
  // 2.39:1 em 1920px largura → altura conteúdo = 803px → barra = (1080-803)/2 = 138px
  // 1080x1920 (vertical): largura 1080 / 2.39 = 452px → barra = (1080-452)/2 = 314px
  const barHeightPct = ratio === "2.39:1" ? 12.8 : ratio === "2.35:1" ? 12.2 : 7.3;

  return (
    <>
      {/* Top bar */}
      <AbsoluteFill
        style={{
          height: `${barHeightPct}%`,
          top: 0,
          bottom: "auto",
          backgroundColor: color,
          pointerEvents: "none",
          zIndex: 100,
        }}
      />
      {/* Bottom bar */}
      <AbsoluteFill
        style={{
          height: `${barHeightPct}%`,
          bottom: 0,
          top: "auto",
          backgroundColor: color,
          pointerEvents: "none",
          zIndex: 100,
        }}
      />
    </>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
